import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import "../styles/Admin.css";
import { Save } from "lucide-react";
import { BACKEND_BASE_URL } from "../config/api";
import {
  createPaymentSetting,
  getActivePaymentSetting,
  updatePaymentSetting
} from "../ServiceAPI/AdminUser/paymentSettingsApi";

const MODE_OPTIONS = [
  "PERSONAL_UPI",
  "BUSINESS_UPI",
  "RAZORPAY",
  "CASHFREE",
  "BANK_TRANSFER",
  "CASH"
];

const initialForm = {
  Payment_mode: "PERSONAL_UPI",
  Account_name: "",
  Upi_id: "",
  Merchant_key: "",
  Merchant_secret: "",
  Bank_name: "",
  Account_number: "",
  IFSC_code: ""
};

const PaymentSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingId, setSettingId] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [qrFile, setQrFile] = useState(null);
  const [existingQrImage, setExistingQrImage] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const isAdminAuthenticated = localStorage.getItem("adminAuth") === "true";
    if (!isAdminAuthenticated) {
      navigate("/admin/login");
      return;
    }
  }, [navigate]);

  useEffect(() => {
    const loadSetting = async () => {
      try {
        setLoading(true);
        const response = await getActivePaymentSetting();
        const setting = response?.data || null;
        if (setting) {
          setSettingId(setting.Id);
          setFormData({
            Payment_mode: setting.Payment_mode || "PERSONAL_UPI",
            Account_name: setting.Account_name || "",
            Upi_id: setting.Upi_id || "",
            Merchant_key: setting.Merchant_key || "",
            Merchant_secret: setting.Merchant_secret || "",
            Bank_name: setting.Bank_name || "",
            Account_number: setting.Account_number || "",
            IFSC_code: setting.IFSC_code || ""
          });
          setExistingQrImage(setting.Qr_image || "");
          setShowForm(false);
        } else {
          setSettingId(null);
          setFormData(initialForm);
          setExistingQrImage("");
          setShowForm(true);
        }
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load payment setting");
      } finally {
        setLoading(false);
      }
    };

    loadSetting();
  }, []);

  const isUpiMode = useMemo(
    () => ["PERSONAL_UPI", "BUSINESS_UPI"].includes(formData.Payment_mode),
    [formData.Payment_mode]
  );
  const isGatewayMode = useMemo(
    () => ["RAZORPAY", "CASHFREE"].includes(formData.Payment_mode),
    [formData.Payment_mode]
  );
  const isBankMode = useMemo(
    () => formData.Payment_mode === "BANK_TRANSFER",
    [formData.Payment_mode]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQrFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setQrFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value ?? "");
      });
      if (qrFile) {
        payload.append("qrImage", qrFile);
      } else if (existingQrImage) {
        payload.append("Qr_image", existingQrImage);
      }

      if (settingId) {
        const response = await updatePaymentSetting(settingId, payload);
        setExistingQrImage(response?.data?.Qr_image || existingQrImage);
      } else {
        const response = await createPaymentSetting(payload);
        setSettingId(response?.data?.Id || null);
        setExistingQrImage(response?.data?.Qr_image || "");
      }
      setQrFile(null);
      setShowForm(false);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save payment setting");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading payment settings...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Payment Settings</h1>
          <p className="admin-page-subtitle">
            {settingId
              ? "Edit active payment setting (single active row mode)"
              : "Create payment setting (only one active row allowed)"}
          </p>
        </div>
      </div>

      {error && <div className="alert alert-error"><span>{error}</span></div>}

      <div className="admin-content-area">
        <div className="data-table-card">
          <div className="data-table-header">
            <h3>Active Payment Setting</h3>
            <button
              type="button"
              className="btn-primary-modern"
              onClick={() => setShowForm((prev) => !prev)}
            >
              {showForm ? "Hide Form" : settingId ? "Edit Setting" : "Create Setting"}
            </button>
          </div>

          <div className="table-responsive" style={{ padding: "0 1rem 1rem 1rem" }}>
            <table className="modern-table compact">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Mode</th>
                  <th>Account Name</th>
                  <th>UPI ID</th>
                  <th>Bank Name</th>
                  <th>Account Number</th>
                  <th>IFSC</th>
                  <th>QR</th>
                </tr>
              </thead>
              <tbody>
                {settingId ? (
                  <tr>
                    <td>{settingId}</td>
                    <td>{formData.Payment_mode || "-"}</td>
                    <td>{formData.Account_name || "-"}</td>
                    <td>{formData.Upi_id || "-"}</td>
                    <td>{formData.Bank_name || "-"}</td>
                    <td>{formData.Account_number || "-"}</td>
                    <td>{formData.IFSC_code || "-"}</td>
                    <td>
                      {existingQrImage ? (
                        <a
                          href={`${BACKEND_BASE_URL}/${existingQrImage}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View QR
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center" }}>No active payment setting found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="modal-form-modern" style={{ padding: "1rem" }}>
            <div className="form-group-modern">
              <label>Payment Mode *</label>
              <select name="Payment_mode" value={formData.Payment_mode} onChange={handleInputChange} required>
                {MODE_OPTIONS.map((mode) => (
                  <option key={mode} value={mode}>{mode}</option>
                ))}
              </select>
            </div>

            <div className="form-group-modern">
              <label>Account Name</label>
              <input name="Account_name" value={formData.Account_name} onChange={handleInputChange} />
            </div>

            {isUpiMode && (
              <div className="form-group-modern">
                <label>UPI ID *</label>
                <input name="Upi_id" value={formData.Upi_id} onChange={handleInputChange} required={isUpiMode} />
              </div>
            )}

            <div className="form-group-modern">
              <label>QR Image File</label>
              <input type="file" accept="image/*" onChange={handleQrFileChange} />
              {existingQrImage && !qrFile && (
                <p style={{ marginTop: "0.5rem", fontSize: "0.9rem" }}>
                  Current file: {existingQrImage}
                </p>
              )}
              {existingQrImage && (
                <img
                  src={`${BACKEND_BASE_URL}/${existingQrImage}`}
                  alt="QR preview"
                  style={{ marginTop: "0.75rem", maxWidth: "220px", borderRadius: "8px", border: "1px solid #e5e7eb" }}
                />
              )}
            </div>

            {isGatewayMode && (
              <>
                <div className="form-group-modern">
                  <label>Merchant Key *</label>
                  <input name="Merchant_key" value={formData.Merchant_key} onChange={handleInputChange} required={isGatewayMode} />
                </div>
                <div className="form-group-modern">
                  <label>Merchant Secret *</label>
                  <input name="Merchant_secret" value={formData.Merchant_secret} onChange={handleInputChange} required={isGatewayMode} />
                </div>
              </>
            )}

            {isBankMode && (
              <>
                <div className="form-group-modern">
                  <label>Bank Name *</label>
                  <input name="Bank_name" value={formData.Bank_name} onChange={handleInputChange} required={isBankMode} />
                </div>
                <div className="form-group-modern">
                  <label>Account Number *</label>
                  <input name="Account_number" value={formData.Account_number} onChange={handleInputChange} required={isBankMode} />
                </div>
                <div className="form-group-modern">
                  <label>IFSC Code *</label>
                  <input name="IFSC_code" value={formData.IFSC_code} onChange={handleInputChange} required={isBankMode} />
                </div>
              </>
            )}

            <div className="modal-footer-modern">
              <button type="submit" className="btn-primary-modern" disabled={saving}>
                <Save size={18} />
                {saving ? "Saving..." : settingId ? "Update Setting" : "Create Setting"}
              </button>
            </div>
            </form>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default PaymentSettings;
